import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { GRAY_300, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { MUIInputField, StyledMUIInput } from 'components/common/MUIInputField'
import {
  RedesignMinimumUnitsForPropertyType,
  redesignDefaultFloors,
  useMeasurements
} from './MeasurementStep.context'
import {
  PropertyTypeCards,
  ResidentialUnitsBuilder
} from 'components/common/MeasurementControls'
import { MUICheckCard } from 'components/common/MUICards/MUICheckCard'
import { SelectableButtonsGrid, SelectableButtonsGridValue } from 'components/common/SelectableButtonsGrid'
import { Trans, useTranslation } from 'react-i18next'

import { AtticScope, CellarScope, MeasurementOnSiteAdditionalInformationType, MeasurementOnSitePropertyType } from 'models/purchaseFlow/measurementDTO'
import { MeasurementLevelConfig, atticLevelRate, cellarWholeRate } from 'components/common/MeasurementControls/measurementHintPricing'
import { logger } from 'utils/logging'

/** The included-floor count the "2nd floor / 3rd+" ordinals of house_floor_price_hint are written for */
const HOUSE_FLOOR_HINT_COPY_INCLUDED_FLOORS = 2

import { CheckboxItem } from 'components/common/CheckboxItem'
import { Label } from 'components/common/Label'
import { MUIInputFieldButton } from 'components/common/MUIInputField/MUIInputFieldButton'
import { MUIInputFieldCounter } from 'components/common/MUIInputFieldCounter'
import { Nullable } from 'models/helpers'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { formatPrice } from 'utils/price'
import { useMeasurementRateCard } from './useMeasurementRateCard'

/**
 * Controller for the redesigned On-Site Area Report configuration (ISSUE-2187, epic ISSUE-1595).
 * Rendered by MeasurementStepController.
 *
 * Pricing cutover (ISSUE-2188): unlike the legacy flow, no measured-extra option products are
 * attached to the cart — the backend pricing engine computes the price from the measurement
 * configuration (quote endpoint for the estimate, engine-at-placement for the order). Price hints
 * come from the backend rate card (GET /order/configuration/measurement/pricing, ISSUE-2702) —
 * the same SF-fed source the engine reads — and show that card's own amounts, so a pill always
 * promises what the cost panel will bill for the matching line (ISSUE-2861). The attic's level is the
 * only engine rule the FE still resolves (measurementHintPricing.ts).
 * Hints render only once the rate card is loaded; the inputs never wait for it.
 *
 * @example
 * <MeasurementStepRedesignController />
 */
export const MeasurementStepRedesignController: FC = () => {
  const { t } = useTranslation(['purchase_flow'], { keyPrefix: 'measurement_step' })

  const {
    isUnitBasedPropertyType,
    isMeasurementAddOnsEnabled,
    isExpressServiceOffered,
    isAdditionalHouseViewsOffered,
    isExpressServiceSelected,
    isAdditionalHouseViewsSelected,
    setIsExpressServiceSelected,
    setIsAdditionalHouseViewsSelected,
    additionalInformation,
    surfaceArea,
    numberOfFloors,
    cellarScope,
    atticScope,
    residentialUnits,
    measurementComments,
    measurementPropertyType,
    setSurfaceArea,
    setNumberOfFloors,
    setCellarScope,
    setAtticScope,
    setResidentialUnits,
    setMeasurementComments,
    setMeasurementPropertyType,
    setAdditionalInformation
  } = useMeasurements()

  const previousPropertyTypeRef = useRef<Nullable<MeasurementOnSitePropertyType>>(null)
  // Carries the configured units over when adding a 3rd unit switches Two-family house to Multi-unit building
  const pendingUnitsRef = useRef<Nullable<number[]>>(null)

  const areaFieldRef = useRef<HTMLDivElement | null>(null)
  const [isAreaFieldTouched, setIsAreaFieldTouched] = useState(false)
  // The quote (and Next, see PurchaseFlowUtilities) needs a floor area — mark the field once the user moves
  // on to ANY other control while it is still empty, so the mandatory-field error renders until a value is entered
  const showAreaRequiredError = isAreaFieldTouched && surfaceArea === 0

  // Document-level on purpose: the disabled Next button sits outside this subtree and swallows its
  // click (pointer-events: none re-targets it to the footer), so a subtree capture handler never
  // sees the one interaction that most needs the error explained
  useEffect(() => {
    if (surfaceArea > 0 || isAreaFieldTouched) return

    const flagInteractionOutsideAreaField = (event: Event) => {
      if (areaFieldRef.current?.contains(event.target as Node)) return
      setIsAreaFieldTouched(true)
    }

    document.addEventListener('click', flagInteractionOutsideAreaField, true)
    document.addEventListener('focusin', flagInteractionOutsideAreaField)

    return () => {
      document.removeEventListener('click', flagInteractionOutsideAreaField, true)
      document.removeEventListener('focusin', flagInteractionOutsideAreaField)
    }
  }, [surfaceArea, isAreaFieldTouched])

  const isHouseType = measurementPropertyType === MeasurementOnSitePropertyType.SINGLE_FAMILY_HOUSE
  const isTwoFamilyHouseType = measurementPropertyType === MeasurementOnSitePropertyType.SEMI_DETACHED_HOUSE

  const pricingQuery = useMeasurementRateCard()
  const pricing = pricingQuery.data?.data

  const totalFloors = isUnitBasedPropertyType
    ? residentialUnits.reduce((sum, unitFloors) => sum + unitFloors, 0)
    : numberOfFloors

  // Hints read the rate card directly, so a pill always promises the amount the cost panel will bill for that
  // line. Two rates the card cannot resolve on its own depend on where the level sits in the building: the
  // attic in every category, the cellar on a House (measurementHintPricing.ts)
  const levelConfig: MeasurementLevelConfig = { propertyType: measurementPropertyType, totalFloors, cellarScope }

  // Tripwire: the "2nd floor / 3rd+" ordinals in house_floor_price_hint hard-code the included-floor
  // count — on a BE retune the amounts would stay correct but the words would lie, so the hint hides
  // and Sentry flags that the copy needs a rewrite in all locales
  const isHouseFloorHintCopyAligned = pricing?.houseFloorsIncluded === HOUSE_FLOOR_HINT_COPY_INCLUDED_FLOORS
  useEffect(() => {
    if (pricing && !isHouseFloorHintCopyAligned) {
      logger.warn('Rate card houseFloorsIncluded diverged from the house floor hint copy ordinals — hint hidden until property_details.house_floor_price_hint is reworded', undefined, {
        context: {
          component: 'MeasurementStepRedesignController',
          metadata: { houseFloorsIncluded: pricing.houseFloorsIncluded }
        }
      })
    }
  }, [pricing, isHouseFloorHintCopyAligned])

  const additionalUnitPrice = pricing ? formatPrice(pricing.additionalResidentialUnit) : null

  // Offered-ness comes from the context (it owns the selections and both payloads); these only format the amount
  const expressServicePrice = isExpressServiceOffered && pricing?.expressService ? formatPrice(pricing.expressService) : null
  const additionalHouseViewsPrice = isAdditionalHouseViewsOffered && pricing?.additionalHouseViews ? formatPrice(pricing.additionalHouseViews) : null

  // Two-family house is priced as 2 units (first unit + 1 additional) and both rows are part of the category,
  // so neither is tagged "extra" — which left the additional-unit rate invisible for Kat. 3 (ISSUE-2861).
  // The add tile is the one place it belongs: it prices the unit the client is about to add.
  const addUnitCaption = isTwoFamilyHouseType
    ? t(
      additionalUnitPrice ? 'property_details.add_extra_unit_switch_price' : 'property_details.add_extra_unit_switch',
      { price: additionalUnitPrice }
    )
    : additionalUnitPrice && t('property_details.add_extra_unit_price', { price: additionalUnitPrice })

  const handleAddUnit = useCallback(() => {
    if (measurementPropertyType === MeasurementOnSitePropertyType.SEMI_DETACHED_HOUSE) {
      // Adding a 3rd unit to a Two-family house switches the property to Multi-unit building (per design)
      pendingUnitsRef.current = [...residentialUnits, 1]
      setMeasurementPropertyType(MeasurementOnSitePropertyType.APARTMENT_BUILDING)
      return
    }

    setResidentialUnits([...residentialUnits, 1])
  }, [measurementPropertyType, residentialUnits, setMeasurementPropertyType, setResidentialUnits])

  const handleRemoveUnit = useCallback((unitIndex: number) => {
    const remainingUnits = residentialUnits.filter((_, index) => index !== unitIndex)
    const minUnits = RedesignMinimumUnitsForPropertyType.get(measurementPropertyType) ?? 1

    if (remainingUnits.length >= minUnits) {
      setResidentialUnits(remainingUnits)
      return
    }

    // Dropping below a category's minimum lands in the category below it, mirroring handleAddUnit:
    // Multi-unit building → Two-family house → House. A one-unit property is not priced per unit at all.
    if (measurementPropertyType === MeasurementOnSitePropertyType.APARTMENT_BUILDING) {
      pendingUnitsRef.current = remainingUnits
      setMeasurementPropertyType(MeasurementOnSitePropertyType.SEMI_DETACHED_HOUSE)
      return
    }

    // Landing on House starts from the House default of 2 floors, like selecting the card would —
    // round 1 carried the remaining unit's floor count over, round 2 decided the category default wins (ISSUE-2861)
    setMeasurementPropertyType(MeasurementOnSitePropertyType.SINGLE_FAMILY_HOUSE)
  }, [measurementPropertyType, residentialUnits, setMeasurementPropertyType, setResidentialUnits])

  // A single-floor House with nothing CHARGED — no whole cellar, no measured attic — is priced by the
  // engine at the apartment base rate (the Kat. 1 fallback in MeasurementQuoteService.houseItems; since
  // ISSUE-2882 a free cellar room or a present-but-unmeasured level no longer breaks it), so the selection
  // jumps to Kat. 1 to keep the card honest instead of showing a House billed as an apartment (ISSUE-2861)
  const collapseBareSingleFloorHouse = useCallback((floors: number, cellar: CellarScope, attic: AtticScope) => {
    if (!isHouseType || floors !== 1 || cellar === CellarScope.WHOLE || attic === AtticScope.MEASURED) return
    setMeasurementPropertyType(MeasurementOnSitePropertyType.APARTMENT_SINGLE_FLOORS)
  }, [isHouseType, setMeasurementPropertyType])

  const handleFloorsChange = useCallback((floors: number) => {
    setNumberOfFloors(floors)
    collapseBareSingleFloorHouse(floors, cellarScope, atticScope)
  }, [setNumberOfFloors, collapseBareSingleFloorHouse, cellarScope, atticScope])

  // Checking defaults to the measured scope, mirroring the pre-scope behaviour; unchecking the last
  // additional area of a single-floor House leaves the bare Kat. 1 configuration, so it collapses too
  const handleCellarToggle = useCallback(() => {
    const nextScope = cellarScope === CellarScope.NONE ? CellarScope.WHOLE : CellarScope.NONE
    setCellarScope(nextScope)
    collapseBareSingleFloorHouse(numberOfFloors, nextScope, atticScope)
  }, [cellarScope, setCellarScope, collapseBareSingleFloorHouse, numberOfFloors, atticScope])

  const handleAtticToggle = useCallback(() => {
    const nextScope = atticScope === AtticScope.NONE ? AtticScope.MEASURED : AtticScope.NONE
    setAtticScope(nextScope)
    collapseBareSingleFloorHouse(numberOfFloors, cellarScope, nextScope)
  }, [atticScope, setAtticScope, collapseBareSingleFloorHouse, numberOfFloors, cellarScope])

  // Pill switches can land on the free scopes, which the engine prices as the bare Kat. 1 configuration —
  // so a Whole cellar → One room switch on a single-floor House collapses too (ISSUE-2882)
  const handleCellarScopeSelect = useCallback((scope: CellarScope) => {
    setCellarScope(scope)
    collapseBareSingleFloorHouse(numberOfFloors, scope, atticScope)
  }, [setCellarScope, collapseBareSingleFloorHouse, numberOfFloors, atticScope])

  const handleAtticScopeSelect = useCallback((scope: AtticScope) => {
    setAtticScope(scope)
    collapseBareSingleFloorHouse(numberOfFloors, cellarScope, scope)
  }, [setAtticScope, collapseBareSingleFloorHouse, numberOfFloors, cellarScope])

  // Reset the configuration to the category defaults when the property type changes. First entry needs no branch
  // here — the provider owns the per-type minimum and the floor default (MeasurementStep.context).
  useEffect(() => {
    const minUnits = RedesignMinimumUnitsForPropertyType.get(measurementPropertyType)
    const isTypeSwitch = previousPropertyTypeRef.current !== null && previousPropertyTypeRef.current !== measurementPropertyType
    previousPropertyTypeRef.current = measurementPropertyType

    if (isTypeSwitch) {
      if (minUnits) {
        setResidentialUnits(pendingUnitsRef.current ?? Array.from({ length: minUnits }, () => 1))
      } else {
        setResidentialUnits([])
        setNumberOfFloors(redesignDefaultFloors(measurementPropertyType))
      }
    }

    pendingUnitsRef.current = null

    // Only trigger when the property type changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measurementPropertyType])

  // Scope pills (ISSUE-2459): ROOM is measured but free, only WHOLE is charged, PRESENT_NOT_MEASURED
  // is captured for the invoice and the on-site technician without being measured
  const cellarScopeOptions: SelectableButtonsGridValue<CellarScope>[] = [
    {
      value: CellarScope.ROOM,
      displayName: t('property_details.cellar_scope_room')
    },
    {
      value: CellarScope.WHOLE,
      displayName: (
        <>
          {t('property_details.cellar_scope_whole')}{' '}

          {!!pricing &&
            <Typography component="span" variant="text-sm" color={GRAY_700}>
              <Trans
                t={t}
                i18nKey="property_details.extra_price"
                values={{ price: formatPrice(cellarWholeRate(levelConfig, pricing), pricing.currency) }}
                components={{ strong: <strong /> }}
              />
            </Typography>
          }
        </>
      )
    },
    {
      value: CellarScope.PRESENT_NOT_MEASURED,
      displayName: t('property_details.scope_not_measured')
    }
  ]

  const atticScopeOptions: SelectableButtonsGridValue<AtticScope>[] = [
    {
      value: AtticScope.MEASURED,
      displayName: (
        <>
          {t('property_details.attic_scope_measured')}{' '}

          {!!pricing &&
            <Typography component="span" variant="text-sm" color={GRAY_700}>
              <Trans
                t={t}
                i18nKey="property_details.extra_price"
                values={{ price: formatPrice(atticLevelRate(levelConfig, pricing), pricing.currency) }}
                components={{ strong: <strong /> }}
              />
            </Typography>
          }
        </>
      )
    },
    {
      value: AtticScope.PRESENT_NOT_MEASURED,
      displayName: t('property_details.scope_not_measured')
    }
  ]

  return (
    <>

      {/* Property type selection (Kat. 1-4) */}
      <PropertyTypeCards selectedType={measurementPropertyType} onSelect={setMeasurementPropertyType} />

      {/* Property details */}
      <SectionedBorderBox title={t('property_details.title')}>
        <Stack gap="1.8rem">

          {/* Total floor area */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" gap=".8rem">

            <div ref={areaFieldRef} style={{ display: 'contents' }}>
              <MUIInputField
                required
                inputBoxWidth="16rem"
                label={t('property_details.total_floor_area')}
                isError={showAreaRequiredError}
                errorText={t('property_details.total_floor_area_required')}
                customInput={
                  <>
                    <StyledMUIInput
                      value={surfaceArea}
                      error={showAreaRequiredError}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '')
                        setSurfaceArea(value ? parseInt(value) : 0)
                      }}
                    />
                    <MUIInputFieldButton sx={{ padding: '0 1.2rem' }}>
                      <Typography variant="text-md" color={GRAY_900} fontWeight="medium">m²</Typography>
                    </MUIInputFieldButton>
                  </>
                }
              />
            </div>

            {!!pricing &&
              <Typography variant="text-sm" color={GRAY_700} paddingBottom="1rem" component="div">
                <Trans
                  t={t}
                  i18nKey="property_details.total_floor_area_hint"
                  values={{
                    price: formatPrice(pricing.areaSurchargeRatePerSqm),
                    threshold: pricing.areaSurchargeThresholdSqm
                  }}
                  components={{ strong: <strong /> }}
                />
              </Typography>
            }

          </Stack>

          {/* Number of floors — Apartment (Kat. 1) & House (Kat. 2). The house counter still excludes the
              cellar and attic (they are priced as levels of the same building), but spelling out "(above
              ground)" confused clients, so both categories keep the plain label (ISSUE-2861 QA round 1) */}
          {!isUnitBasedPropertyType &&
            <Stack direction="row" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" gap=".8rem">

              <MUIInputFieldCounter
                minValue={1}
                counterValue={numberOfFloors}
                counterOnChange={handleFloorsChange}
                inputBoxWidth="14rem"
                label={t('property_details.number_of_floors')}
              />

              {!!pricing && (!isHouseType || isHouseFloorHintCopyAligned) &&
                <Typography variant="text-sm" color={GRAY_700} paddingBottom="1rem" component="div">
                  {/* The "2nd floor / 3rd+" ordinals mirror houseFloorsIncluded — gated above so a BE retune hides the hint instead of letting it lie */}
                  {isHouseType &&
                    <Trans
                      t={t}
                      i18nKey="property_details.house_floor_price_hint"
                      values={{
                        secondFloorPrice: formatPrice(pricing.housePerFloor),
                        price: formatPrice(pricing.additionalFloor)
                      }}
                      components={{ strong: <strong /> }}
                    />
                  }

                  {!isHouseType &&
                    <Trans
                      t={t}
                      i18nKey="property_details.floor_price_hint"
                      values={{ price: formatPrice(pricing.additionalFloor) }}
                      components={{ strong: <strong /> }}
                    />
                  }
                </Typography>
              }

            </Stack>
          }

          {/* Residential units — Two-family house (Kat. 3) & Multi-unit building (Kat. 4) */}
          {isUnitBasedPropertyType &&
            <ResidentialUnitsBuilder
              units={residentialUnits}
              // Only the first unit is locked. Every other unit has a defined landing when removed — a Multi-unit
              // building falls back to Two-family house, a Two-family house to House (ISSUE-2861) — so the whole
              // ladder the add button climbs is reversible
              minUnits={1}
              // The "extra +price" tag only marks units beyond the CURRENT type's own minimum, so a Multi-unit
              // building's 3 default units are not tagged "extra" (matches the Two-family 2-unit convention)
              extraUnitFromIndex={RedesignMinimumUnitsForPropertyType.get(measurementPropertyType) ?? 1}
              extraUnitPriceFormatted={additionalUnitPrice}
              addUnitCaption={addUnitCaption}
              floorPriceHint={pricing &&
                <Trans
                  t={t}
                  i18nKey="property_details.unit_floor_price_hint"
                  values={{ price: formatPrice(pricing.additionalFloor) }}
                  components={{ strong: <strong /> }}
                />
              }
              onUnitsChange={setResidentialUnits}
              onAddUnit={handleAddUnit}
              onRemoveUnit={handleRemoveUnit}
            />
          }

          {/* Additional areas to measure */}
          <Stack gap=".6rem">

            <Label text={t('property_information.additional_area')} />

            <Stack gap=".8rem">

              {/* The card checkbox captures presence; the pills capture the measurement scope (ISSUE-2459).
                  Checking defaults to the measured scope, mirroring the pre-scope behaviour.
                  Cellar renders BEFORE attic (ISSUE-2861): a measured cellar shifts the attic to the building's
                  3rd level and therefore its price, so the cellar has to be answered first for the attic pill to
                  show the price the client will actually be charged */}
              <MUICheckCard
                title={t('property_details.cellar_title')}
                description={t('property_details.cellar_description')}
                checked={cellarScope !== CellarScope.NONE}
                onCheck={handleCellarToggle}
                expandableContent={
                  <Stack gap=".6rem" sx={{ borderTop: `1px solid ${GRAY_300}`, paddingTop: '1.2rem' }}>

                    <Typography variant="text-sm" color={GRAY_700}>{t('property_details.cellar_scope_question')}</Typography>

                    <SelectableButtonsGrid
                      values={cellarScopeOptions}
                      selectedValue={cellarScope}
                      onSelect={handleCellarScopeSelect}
                    />

                  </Stack>
                }
              />

              <MUICheckCard
                title={t('property_details.attic_title')}
                description={t('property_details.attic_description')}
                checked={atticScope !== AtticScope.NONE}
                onCheck={handleAtticToggle}
                expandableContent={
                  <Stack gap=".6rem" sx={{ borderTop: `1px solid ${GRAY_300}`, paddingTop: '1.2rem' }}>

                    <Typography variant="text-sm" color={GRAY_700}>{t('property_details.attic_scope_question')}</Typography>

                    <SelectableButtonsGrid
                      values={atticScopeOptions}
                      selectedValue={atticScope}
                      onSelect={handleAtticScopeSelect}
                    />

                  </Stack>
                }
              />

            </Stack>

          </Stack>

          {/* Add-ons (ISSUE-2838) */}
          {isMeasurementAddOnsEnabled && (!!expressServicePrice || !!additionalHouseViewsPrice) &&
            <Stack gap=".6rem">

              <Label text={t('additional_options.title')} />

              <Stack gap=".8rem">

                {!!expressServicePrice &&
                  <MUICheckCard
                    title={t('additional_options.express_title')}
                    // The delivery-timing disclaimer shows whether or not the add-on is picked
                    description={t('additional_options.express_description')}
                    checked={isExpressServiceSelected}
                    trailing={
                      <Typography variant="text-sm" color={GRAY_700} whiteSpace="nowrap" component="div">
                        <Trans
                          t={t}
                          i18nKey="additional_options.add_on_price"
                          values={{ price: expressServicePrice }}
                          components={{ strong: <strong /> }}
                        />
                      </Typography>
                    }
                    onCheck={() => setIsExpressServiceSelected(!isExpressServiceSelected)}
                  />
                }

                {!!additionalHouseViewsPrice &&
                  <MUICheckCard
                    title={t('additional_options.house_views_title')}
                    description={t('additional_options.house_views_description')}
                    checked={isAdditionalHouseViewsSelected}
                    trailing={
                      <Typography variant="text-sm" color={GRAY_700} whiteSpace="nowrap" component="div">
                        <Trans
                          t={t}
                          i18nKey="additional_options.add_on_price"
                          values={{ price: additionalHouseViewsPrice }}
                          components={{ strong: <strong /> }}
                        />
                      </Typography>
                    }
                    onCheck={() => setIsAdditionalHouseViewsSelected(!isAdditionalHouseViewsSelected)}
                  />
                }

              </Stack>

            </Stack>
          }

          {/* Reason for the measurement — restored from the legacy flow; missing from the redesign screens without a recorded decision (raised on epic ISSUE-1595) */}
          <Stack gap=".6rem">

            <Label text={t('additional_information.description')} />

            <Stack gap=".4rem">
              {Object.values(MeasurementOnSiteAdditionalInformationType).map(type => (
                <CheckboxItem
                  key={type}
                  title={t(`additional_information_type.${type}`)}
                  checked={additionalInformation.has(type)}
                  onClick={() =>
                    additionalInformation.has(type)
                      ? setAdditionalInformation(new Set([...additionalInformation].filter(item => item !== type)))
                      : setAdditionalInformation(new Set([...additionalInformation, type]))
                  }
                />
              ))}
            </Stack>

          </Stack>

          {/* Comments */}
          <Stack gap=".6rem">

            <Label text={t('comments.title')} />

            <MUIInputField
              isMultiline
              placeholder={t('property_details.comments_placeholder')}
              value={measurementComments}
              onChange={(e) => setMeasurementComments(e.target.value)}
            />

          </Stack>

        </Stack>
      </SectionedBorderBox>

    </>
  )
}
